/**
 * @file components/log/VitalsModal.jsx
 * @description Refined modal with smart goal weight recommendation
 */

import { Activity, Lightbulb, Loader2, Save, X, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { useProfile } from "../../hooks/useProfile"; // Relative path
import { useUpdateProfile } from "../../hooks/useProfileMutations";
import { calculateRecommendedGoal } from "../../utils/Calculategoalweight";
import { ProfileInput } from "../shared/ProfileInput";

export default function VitalsModal({ uid, onClose }) {
  // 1. Hook-based data & mutation
  const { data: profileData, isLoading: fetching } = useProfile(uid);
  const updateProfile = useUpdateProfile(uid);

  // These map directly to your existing UI variables
  const loading = updateProfile.isPending;
  const [profile, setProfile] = useState({});
  const [gender, setGender] = useState("male");

  useEffect(() => {
    if (profileData) {
      // Wrap in a timeout to move the update out of the current render cycle
      const timer = setTimeout(() => {
        setProfile(profileData);
        setGender(profileData.gender || "male");
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [profileData]);
  // Keep your existing calculation effect exactly as it was
  // ADD THIS instead
  const recommendedGoal =
    profile?.height && profile?.weight && profile?.age && profile?.activityLevel
      ? calculateRecommendedGoal({
          weight: profile.weight,
          height: profile.height,
          age: profile.age,
          gender,
          activityLevel: profile.activityLevel,
        })
      : null;

  // Updated save function using the mutation
  async function handleSaveProfile() {
    if (!uid) return;
    updateProfile.mutate(
      { ...profile, gender },
      {
        onSuccess: () => onClose(),
        onError: (err) => {
          console.error(err);
          alert("Failed to update profile.");
        },
      },
    );
  }
  const applyGoal = (goalWeight) => {
    setProfile((prev) => ({ ...prev, goalWeight: Number(goalWeight) }));
  };

  // Validation ranges
  const LIMITS = {
    height: { min: 50, max: 280, label: "Height" },
    weight: { min: 20, max: 350, label: "Weight" },
    age: { min: 10, max: 120, label: "Age" },
    goalWeight: { min: 20, max: 350, label: "Goal weight" },
  };

  function getValidationErrors() {
    const errors = [];
    for (const [field, { min, max, label }] of Object.entries(LIMITS)) {
      const val = profile?.[field];
      if (val && (val < min || val > max)) {
        errors.push(`${label} must be between ${min}–${max}`);
      }
    }
    if (profile?.goalWeight && profile?.weight && profile.goalWeight > profile.weight * 1.5) {
      errors.push("Goal weight seems unrealistically high");
    }
    if (profile?.goalWeight && profile?.weight && profile.goalWeight < profile.weight * 0.4) {
      errors.push("Goal weight seems unrealistically low");
    }
    if (profile?.age && profile.age % 1 !== 0) {
      errors.push("Age must be a whole number");
    }
    return errors;
  }

  const validationErrors = getValidationErrors();

  const isDisabeled =
    loading ||
    !profile?.height ||
    !profile?.weight ||
    !profile?.age ||
    !profile?.goalWeight ||
    !profile?.activityLevel ||
    validationErrors.length > 0;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-xl"
        onClick={() => {
          if (!loading) onClose();
        }}
      />

      <div className="relative bg-[#0F1115] border border-white/10 w-full max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="p-8 pb-4 flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-brand-green/10 rounded-2xl text-brand-green">
              <Activity size={20} />
            </div>
            <div>
              <h2 className="font-serif text-2xl text-white tracking-tight">
                Body Vitals
              </h2>
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
                Foundation Metrics
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              if (!loading) onClose();
            }}
            className="p-2 hover:bg-white/5 rounded-full text-gray-500 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-8 pt-4 space-y-6 max-h-[80vh] overflow-y-auto">
          {fetching ? (
            <div className="py-12 flex flex-col items-center gap-3 text-gray-600">
              <Loader2 className="animate-spin" size={24} />
              <p className="text-[10px] font-bold uppercase tracking-widest">
                Loading metrics...
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <ProfileInput
                  label="Height (cm)"
                  value={profile?.height ?? ""}
                  onChange={(v) =>
                    setProfile({ ...profile, height: Number(v) || "" })
                  }
                  hint="50–280"
                />
                <ProfileInput
                  label="Weight (kg)"
                  value={profile?.weight ?? ""}
                  onChange={(v) =>
                    setProfile({ ...profile, weight: Number(v) || "" })
                  }
                  hint="20–350"
                />
                <ProfileInput
                  label="Age"
                  value={profile?.age ?? ""}
                  onChange={(v) =>
                    setProfile({ ...profile, age: Number(v) || "" })
                  }
                  hint="10–120"
                />
                <ProfileInput
                  label="Goal (kg)"
                  value={profile?.goalWeight ?? ""}
                  onChange={(v) =>
                    setProfile((prev) => ({
                      ...prev,
                      goalWeight: Number(v) || "",
                    }))
                  }
                  hint="20–350"
                />
              </div>

              {/* Gender Selection */}
              <div className="space-y-3">
                <label className="text-xs font-bold uppercase tracking-widest text-gray-400 block">
                  Gender
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { value: "male", label: "Male" },
                    { value: "female", label: "Female" },
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setGender(option.value)}
                      className={`p-3 rounded-xl border transition-all text-center ${
                        gender === option.value
                          ? "border-brand-green bg-brand-green/10 text-brand-green"
                          : "border-white/10 bg-white/[0.02] text-gray-400 hover:border-white/20"
                      }`}
                    >
                      <p className="text-xs font-bold">{option.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Recommended Goal Section */}
              {recommendedGoal && (
                <div className="space-y-3 p-4 bg-brand-green/5 border border-brand-green/20 rounded-2xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightbulb size={14} className="text-brand-green" />
                    <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">
                      Recommended Goals
                    </p>
                  </div>

                  {/* Body Fat Info */}
                  <div className="space-y-2 mb-4 pb-4 border-b border-brand-green/10">
                    <div className="flex justify-between text-[10px]">
                      <span className="text-gray-500">Current BF%:</span>
                      <span className="text-brand-green font-bold">
                        {recommendedGoal.currentBF}%
                      </span>
                    </div>
                    <div className="flex justify-between text-[10px]">
                      <span className="text-gray-500">Lean Mass:</span>
                      <span className="text-emerald-400 font-bold">
                        {recommendedGoal.lbm} kg
                      </span>
                    </div>
                  </div>

                  {/* Goal Options */}
                  <div className="space-y-2">
                    {/* Default Goal */}
                    <button
                      onClick={() => applyGoal(recommendedGoal.goalWeight)}
                      className="w-full text-left p-3 rounded-lg bg-white/[0.02] border border-brand-green/30 hover:bg-brand-green/10 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-bold text-brand-green">
                            Balanced Goal
                          </p>
                          <p className="text-[10px] text-gray-500 mt-0.5">
                            {recommendedGoal.healthyRange.min}%-
                            {recommendedGoal.healthyRange.max}% BF
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-black text-brand-green">
                            {recommendedGoal.goalWeight} kg
                          </p>
                          <p className="text-[8px] text-gray-600">Apply</p>
                        </div>
                      </div>
                    </button>

                    {/* Aggressive Goal */}
                    <button
                      onClick={() => applyGoal(recommendedGoal.aggressiveGoal)}
                      className="w-full text-left p-3 rounded-lg bg-white/[0.02] border border-emerald-400/30 hover:bg-emerald-400/10 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-bold text-emerald-400 flex items-center gap-1">
                            <Zap size={12} /> Lean
                          </p>
                          <p className="text-[10px] text-gray-500 mt-0.5">
                            ~{recommendedGoal.healthyRange.min}% BF
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-black text-emerald-400">
                            {recommendedGoal.aggressiveGoal} kg
                          </p>
                          <p className="text-[8px] text-gray-600">Apply</p>
                        </div>
                      </div>
                    </button>

                    {/* Conservative Goal */}
                    <button
                      onClick={() =>
                        applyGoal(recommendedGoal.conservativeGoal)
                      }
                      className="w-full text-left p-3 rounded-lg bg-white/[0.02] border border-amber-400/30 hover:bg-amber-400/10 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-bold text-amber-400">
                            Muscle-focused
                          </p>
                          <p className="text-[10px] text-gray-500 mt-0.5">
                            ~{recommendedGoal.healthyRange.max}% BF
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-black text-amber-400">
                            {recommendedGoal.conservativeGoal} kg
                          </p>
                          <p className="text-[8px] text-gray-600">Apply</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Activity Level Selection */}
              <div className="space-y-3">
                <label className="text-xs font-bold uppercase tracking-widest text-gray-400 block">
                  Activity Level
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    {
                      value: "sedentary",
                      label: "Sedentary",
                      desc: "Little/no exercise",
                    },
                    {
                      value: "lightly_active",
                      label: "Lightly Active",
                      desc: "1-3x/week",
                    },
                    {
                      value: "moderately_active",
                      label: "Moderately Active",
                      desc: "3-5x/week",
                    },
                    {
                      value: "very_active",
                      label: "Very Active",
                      desc: "6-7x/week",
                    },
                  ].map((level) => (
                    <button
                      key={level.value}
                      onClick={() =>
                        setProfile({ ...profile, activityLevel: level.value })
                      }
                      className={`p-3 rounded-xl border transition-all text-center ${
                        profile?.activityLevel === level.value
                          ? "border-brand-green bg-brand-green/10 text-brand-green"
                          : "border-white/10 bg-white/[0.02] text-gray-400 hover:border-white/20"
                      }`}
                    >
                      <p className="text-xs font-bold">{level.label}</p>
                      <p className="text-[8px] text-gray-500 mt-1">
                        {level.desc}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl space-y-1 animate-in fade-in duration-150">
                  {validationErrors.map((err, i) => (
                    <p key={i} className="text-[10px] text-red-400 font-medium">
                      • {err}
                    </p>
                  ))}
                </div>
              )}

              {/* Info Box */}
              <div className="p-4 bg-white/[0.02] border border-white/5 rounded-2xl">
                <p className="text-[10px] leading-relaxed text-gray-500 font-medium italic text-center">
                  These recommendations are based on estimated body fat. For
                  more accuracy, consider an InBody scan or similar assessment.
                </p>
              </div>

              {/* Action Button */}
              <button
                onClick={handleSaveProfile}
                disabled={isDisabeled}
                className="w-full flex items-center justify-center gap-3 py-5 rounded-2xl 
                           bg-brand-green text-[#0F1115] font-bold text-sm uppercase tracking-widest 
                           hover:bg-green-400 transition-all active:scale-[0.98] disabled:opacity-30 
                           shadow-[0_0_20px_rgba(16,185,129,0.15)]"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <>
                    <Save size={18} />
                    <span>Sync Vitals</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
